import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { containerTypesApi } from './container-types.api';
import { containerTypeKeys } from './container-types.queries';
import type {
  CreateContainerTypeRequest,
  UpdateContainerTypeRequest,
  ContainerTypesListParams
} from '../types';

export function useCreateContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (data: CreateContainerTypeRequest) =>
      containerTypesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerTypeKeys.lists() });
    }
  });
}

export function useUpdateContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data: UpdateContainerTypeRequest;
    }) => containerTypesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerTypeKeys.lists() });
    }
  });
}

export function useDeleteContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      containerTypesApi.delete({ id, force }),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerTypeKeys.lists() });
    }
  });
}

export function useBulkDeleteContainerTypesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<ContainerTypesListParams> }
    ) => containerTypesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerTypeKeys.lists() });
    }
  });
}

export function useBulkChangeStatusContainerTypesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[]; status: 'active' | 'inactive' }
        | {
            all: true;
            filters: Partial<ContainerTypesListParams>;
            status: 'active' | 'inactive';
          }
    ) => containerTypesApi.bulkChangeStatus(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkChangeStatusSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: containerTypeKeys.lists() });
    }
  });
}
